/**
 * @module api/sideload
 *
 * RESUMABLE chunked upload for "Upload an episode file" (sideloading).
 *
 * Replaces a single multipart POST that carried the whole file. That shape
 * lost everything on any interruption, because nothing was persisted
 * server-side until the final byte arrived — measured twice against
 * production from a real iPhone (the request died 33s and 45s in with
 * "HPE_INVALID_EOF_STATE", a raw TCP drop).
 *
 * Why interruptions are unavoidable on mobile: iOS Safari SUSPENDS a
 * backgrounded tab's JavaScript and network the moment the user switches
 * apps or the screen locks. The in-flight request is killed, and no web
 * page can prevent it — iOS exposes no background-upload API (Background
 * Fetch is Chrome/Android-only; a Service Worker can't hold a long upload
 * open there either). So the upload genuinely CANNOT continue in the
 * background; the only thing that can be fixed is what an interruption
 * COSTS.
 *
 * Here it costs one chunk. Bytes already accepted are kept server-side, and
 * uploadFileInChunks resumes from the server's own count — waiting for the
 * tab to come back to the foreground first, so returning to the app picks
 * the upload straight back up instead of failing or starting over.
 */
import { api } from "./config.js";

/**
 * Start an upload: creates the episode row (immediately visible, with a
 * spinner) and an upload session, BEFORE any file bytes are sent.
 *
 * @param {Object} params
 * @param {string} params.title
 * @param {string} [params.notes]
 * @param {number[]} [params.tagIds]
 * @param {string} [params.podcastId] - Destination podcast (server defaults to Miscellanea)
 * @param {string} [params.thumbnailUrl]
 * @param {string} params.mimetype
 * @param {number} params.totalBytes
 * @param {Object} [params.itunes] - Optional Apple Podcasts / RSS item fields
 *   ({ episode, season, episodeType, explicit, author, subtitle, summary }).
 *   Validated and coerced server-side; anything invalid is dropped rather
 *   than failing the upload.
 * @param {string} [params.sourceUrl] - Optional page the file came from
 * @returns {Promise<{uploadId: string, episodeId: number, podcastId: string, chunkSize: number}>}
 */
const initSideload = async (params) => {
  try {
    const response = await api.post("/sideload/init", params);
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || "Could not start the upload");
  }
};

/**
 * Ask the server how many bytes it actually has. This — not any local
 * count — is the resume point: a chunk whose response was lost may still
 * have landed, and a chunk that died mid-flight leaves a partial tail the
 * server has already accounted for.
 *
 * @param {string} uploadId
 * @returns {Promise<{receivedBytes: number, totalBytes: number, status: string}>}
 */
const getUploadStatus = async (uploadId) => {
  const response = await api.get(`/sideload/upload/${uploadId}`);
  return response.data.data;
};

/**
 * Resolves once the tab is FOREGROUNDED and the browser reports being
 * online — the precondition for a retry having any chance of succeeding.
 *
 * This is what makes a backgrounded upload recoverable rather than failed:
 * while iOS has the tab suspended, this simply doesn't resolve (the whole
 * JS context is frozen anyway), and the moment the user returns to the app
 * it continues. Without it, the retry would fire into a suspended network
 * stack, burn through the retry budget, and fail the upload for what is
 * really just "the user looked at another app for a minute".
 *
 * @returns {Promise<void>}
 */
const waitUntilForegroundAndOnline = () =>
  new Promise((resolve) => {
    const ready = () => document.visibilityState === "visible" && navigator.onLine;
    if (ready()) {
      resolve();
      return;
    }
    const check = () => {
      if (!ready()) return;
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("online", check);
      resolve();
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("online", check);
  });

/** Consecutive failures tolerated for a SINGLE chunk before giving up. */
const MAX_CHUNK_RETRIES = 5;

/**
 * Upload the file body as sequential chunks, resuming across interruptions.
 *
 * @param {Object} params
 * @param {string} params.uploadId
 * @param {File} params.file
 * @param {number} params.chunkSize - Server-dictated (from initSideload)
 * @param {(percent: number) => void} [params.onProgress] - 0-100
 * @param {() => boolean} [params.isCancelled] - Polled between chunks so a
 *   user cancel stops promptly instead of after the whole file
 * @throws {Error} If a chunk fails MAX_CHUNK_RETRIES times in a row
 */
const uploadFileInChunks = async ({ uploadId, file, chunkSize, onProgress, isCancelled }) => {
  // Always start from the SERVER's count, never 0 — this same call is what
  // makes a resumed upload skip everything already accepted.
  let offset = (await getUploadStatus(uploadId)).receivedBytes;
  let retries = 0;

  while (offset < file.size) {
    if (isCancelled?.()) return;

    // File.slice() is lazy — it does NOT read the bytes into memory here,
    // so this stays flat regardless of how large the file is.
    const chunk = file.slice(offset, Math.min(offset + chunkSize, file.size));

    try {
      const response = await api.put(`/sideload/chunk/${uploadId}`, chunk, {
        headers: {
          // Explicitly binary: the shared api instance defaults to
          // application/json, and (as with the old multipart body) axios
          // will NOT override an inherited instance default on its own.
          "Content-Type": "application/octet-stream",
          "x-chunk-offset": String(offset),
        },
        timeout: 0, // a chunk on a slow connection must not hit the 30s default
      });
      offset = response.data.data.receivedBytes;
      retries = 0; // progress was made — a later failure gets a full budget again
      onProgress?.(Math.floor((offset / file.size) * 100));
    } catch (error) {
      // 409 = the server's offset differs from ours (a chunk landed whose
      // response we never saw, or a partial tail was recorded). Not a
      // failure: adopt the server's number and carry on WITHOUT spending a
      // retry, since this resyncs rather than re-attempts.
      const serverOffset = error.response?.data?.data?.receivedBytes;
      if (error.response?.status === 409 && typeof serverOffset === "number") {
        offset = serverOffset;
        onProgress?.(Math.floor((offset / file.size) * 100));
        continue;
      }

      // A 4xx that isn't an offset mismatch is a real, non-retryable
      // rejection (session gone, size exceeded, ...) — surface it now
      // rather than hammering.
      const status = error.response?.status;
      if (status && status >= 400 && status < 500 && status !== 409) {
        throw new Error(error.response?.data?.error || "Upload rejected by the server");
      }

      // Otherwise: a network-level drop — most often iOS suspending this
      // tab. Wait for the app to be foregrounded and online again, then
      // resync and continue from whatever actually landed.
      retries += 1;
      if (retries > MAX_CHUNK_RETRIES) {
        throw new Error("Upload interrupted too many times — tap Upload to resume");
      }
      await waitUntilForegroundAndOnline();
      if (isCancelled?.()) return;
      offset = (await getUploadStatus(uploadId)).receivedBytes;
      onProgress?.(Math.floor((offset / file.size) * 100));
    }
  }
};

/**
 * Attach the optional thumbnail image to an upload session (small, one-shot
 * — no chunking needed).
 *
 * @param {string} uploadId
 * @param {File} file
 */
const uploadSideloadThumbnail = async (uploadId, file) => {
  const formData = new FormData();
  formData.append("thumbnail", file);
  await api.post(`/sideload/thumbnail/${uploadId}`, formData, {
    // Same inherited-default problem as the chunk PUT above: clear it so the
    // browser computes the real multipart boundary itself.
    headers: { "Content-Type": undefined },
    timeout: 0,
  });
};

/**
 * All bytes are in — hand off to server-side processing (ffprobe / ffmpeg /
 * R2), which continues regardless of what the client does next.
 *
 * @param {string} uploadId
 * @returns {Promise<{episodeId: number}>}
 */
const completeSideload = async (uploadId) => {
  try {
    const response = await api.post(`/sideload/complete/${uploadId}`);
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || "Could not finish the upload");
  }
};

/**
 * Cancel an in-progress upload: drops the partial file and the placeholder
 * episode, so cancelling leaves nothing behind rather than a permanently
 * spinning phantom episode.
 *
 * @param {string} uploadId
 */
const abortSideload = async (uploadId) => {
  await api.delete(`/sideload/upload/${uploadId}`).catch(() => {});
};

// ─── Text to speech ──────────────────────────────────────────────────────────
// The upload page's second mode: the episode is generated from TEXT (fetched
// from a link, or pasted) read aloud server-side (services/tts.js). No chunked
// upload involved — the text is small, and the slow part (speech generation)
// runs in the background after a single request.

/**
 * The voices the server can use, plus the text limits for the form.
 *
 * @returns {Promise<{voices: Array<{id: string, name: string, language: string|null,
 *   languageName: string, gender: string, multilingual?: boolean}>, maxChars: number, charsPerMinute: number}>}
 */
const getTtsVoices = async () => {
  const response = await api.get("/sideload/tts/voices");
  return response.data.data;
};

/**
 * Fetch a web page's article text for the user to review before generating.
 * Nothing is stored server-side.
 *
 * @param {string} url
 * @returns {Promise<{url: string, title: string, text: string, imageUrl: string|null,
 *   language: string|null, languageName: string|null, chars: number, estimatedMinutes: number}>}
 * @throws {Error} With the server's user-facing reason (site refused, no article found, …)
 */
const extractArticleText = async (url) => {
  try {
    // 60s: the server itself gives a slow site 20s per hop, plus redirects.
    const response = await api.post("/sideload/tts/extract", { url }, { timeout: 60000 });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || "Couldn't read that page");
  }
};

/**
 * Create a text-to-speech episode. Returns as soon as the episode row exists
 * and generation is queued — the episode then shows a progress ring in the
 * lists like any download.
 *
 * @param {Object} params - { title, text, sourceUrl, voice, language, format,
 *   notes, tagIds, podcastId, thumbnailUrl, itunes }
 * @param {File} [thumbnailFile] - Optional image (also the MP4's still frame)
 * @returns {Promise<{episodeId: number, podcastId: string, voice: string,
 *   language: string|null, estimatedMinutes: number}>}
 */
const createTtsSideload = async (params, thumbnailFile) => {
  // Multipart, because the optional image travels in the same request.
  const formData = new FormData();
  formData.append("data", JSON.stringify(params));
  if (thumbnailFile) formData.append("thumbnail", thumbnailFile);
  try {
    const response = await api.post("/sideload/tts", formData, {
      // Same inherited-default problem as the thumbnail upload above: clear
      // the instance's JSON Content-Type so the browser sets the boundary.
      headers: { "Content-Type": undefined },
      timeout: 0, // a large image on a slow connection must not hit the 30s default
    });
    return response.data.data;
  } catch (error) {
    throw new Error(error.response?.data?.error || error.message || "Couldn't start text to speech");
  }
};

export const sideloadApi = {
  initSideload,
  getUploadStatus,
  uploadFileInChunks,
  uploadSideloadThumbnail,
  completeSideload,
  abortSideload,
  getTtsVoices,
  extractArticleText,
  createTtsSideload,
};
